import { ErrorInfo, Component, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { Button as CustomButton } from '../button/Button';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Prevent infinite re-renders by checking if error is already set
    if (this.state.hasError && this.state.error === error) {
      return;
    }

    // Enhanced error logging with more context
    const errorContext = {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
        cause: error.cause
      },
      errorInfo: {
        componentStack: errorInfo.componentStack
      },
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId()
    };

    
      console.group('🚨 Error Boundary Caught Error');
      console.error('Error Details:', error);
      console.error('Error Info:', errorInfo);
      console.error('Full Context:', errorContext);
      console.groupEnd();
    

    this.setState({ 
      hasError: true, 
      error, 
      errorInfo 
    });
  }

  private getCurrentUserId = (): string | null => {
    try {
      return localStorage.getItem('userId') || null;
    } catch (error) {
      console.warn('Failed to access localStorage for userId:', error);
      return null;
    }
  };

  private getSessionId = (): string | null => {
    try {
      return sessionStorage.getItem('sessionId') || null;
    } catch (error) {
      console.warn('Failed to access sessionStorage for sessionId:', error);
      return null;
    }
  };

  resetError = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            width: '100%',
            height: '80vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f7fd',
            padding: 4,
            boxSizing: 'border-box',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: '600px',
              textAlign: 'center'
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#121433', 
                fontWeight: 600, 
                mb: 2,
                fontSize: { xs: '1.5rem', md: '2rem' }
              }}
            >
              Oops! Something went wrong
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#3f4159', 
                mb: 4,
                fontSize: '1.1rem',
                lineHeight: 1.6
              }}
            >
              We encountered an unexpected error. Our team has been notified and is working to fix it.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <CustomButton
                onClick={this.resetError}
                variant="contained"
                btnText="Try Again"
                sx={{
                  backgroundColor: '#4e2788',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  '&:hover': {
                    backgroundColor: '#3d1f6b'
                  }
                }}
              />
              <CustomButton
                onClick={() => window.location.reload()}
                variant="outlined"
                btnText="Reload Page"
                sx={{
                  borderColor: '#4e2788',
                  color: '#4e2788',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  '&:hover': {
                    borderColor: '#3d1f6b',
                    backgroundColor: 'rgba(78, 39, 136, 0.04)'
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
